import React from 'react';
import * as CampaignFields from '../Elements'
import * as StyledComponents from '../StyledComponents'
import { CampaignEnablementStatusField } from '../Elements/CampaignEnablementStatusField';
import { useUnifyAdminStore } from '../../AdminStore/index';
import { BUTTON_VARIANTS, Checkbox, SecondaryButton, BUTTON_SIZES } from 'cdk-radial';
import { BrowseOrDragBulkUpload } from './BrowseOrDragBulkUpload';
import { CustomMultiTargetEditor } from './CustomMultiTargetEditor';
import { BulkUploadedDataView } from './BulkUploadedDataView';
import { useCampaignTranslator, campaignTranslationModel, translateCampaignFields } from '../CampaignStore/DataQueries/useCampaignsQueries';
import { ICampaign } from '../CampaignStore/slices/createCampaignSlice';
import { MutationStatus } from '../../AdminStore/slices';
import { useTranslation } from 'react-i18next';

const PreviewButton = (): JSX.Element => {
  const { t } = useTranslation();
  const setIsPreviewCampaignDialogOpen = useUnifyAdminStore((state) => state.setIsPreviewCampaignDialogOpen);
  const setIsCreateOrUpdatePreview = useUnifyAdminStore((state) => state.setIsCreateOrUpdatePreview);

  const onPreviewClick = () => {
    setIsPreviewCampaignDialogOpen(true);
    setIsCreateOrUpdatePreview(true);
  };

  return (
    <StyledComponents.StyledPreviewButton text={t('campaigns.preview')} variant={BUTTON_VARIANTS.TEXT} onClick={onPreviewClick} />
  );
};

interface TabItem {
  id: string;
  panelId: string;
  renderPanel: any;
  renderTab: any;
}

export const CreateCampaignDialogBody = (): JSX.Element => {
  const showFrenchTranslation = useUnifyAdminStore((state) => state.showFrenchTranslation)
  const setShowFrenchTranslation = useUnifyAdminStore((state) => state.setShowFrenchTranslation)
  const showCanadaEnglishTranslation = useUnifyAdminStore((state) => state.showCanadaEnglishTranslation)
  const setShowCanadaEnglishTranslation = useUnifyAdminStore((state) => state.setShowCanadaEnglishTranslation)
  const campaignItems = useUnifyAdminStore((state) => state.campaignItems);
  const permissions = useUnifyAdminStore(state => state.permissions);
  const archiveSwitch = useUnifyAdminStore((state) => state.campaignArchiveSwitch);
  const updateCreateCampaignData = useUnifyAdminStore((state) => state.updateCreateCampaignData);
  const setCustomToastContent = useUnifyAdminStore(state => state.setCustomToastContent);
  const setShowCustomContentToast = useUnifyAdminStore(state => state.setShowCustomContentToast);
  const { t } = useTranslation();

  const keys: (keyof ICampaign)[] = ["description", "name", "linkText"];

  const { mutation } = useCampaignTranslator({ keys });

  const handleTranslation = (lang: "enCA" | "frCA") => {
    const model = campaignTranslationModel(campaignItems, lang, keys);

    mutation.mutate(
      { model, lang },

      {
        onSuccess: (translated) => {
          translateCampaignFields({
            campaignItems,
            translated,
            keys,
            lang,
            updateCreateCampaignData,
            setCustomToastContent,
            setShowCustomContentToast,
          });
        },

        onError: () => {
          setCustomToastContent({
            type: MutationStatus.Error,
            message: "Translation failed. Please try again.",
          });
          setShowCustomContentToast(true);
        },
      }
    );
  };


  let tabs: TabItem[] = [];

  tabs.push({
    id: 'tab-1',
    panelId: 'panel-1',
    renderPanel: (props: any) => (
      <StyledComponents.StyledTabPanel {...props}>
        <CustomMultiTargetEditor readOnlyMode={archiveSwitch} />
      </StyledComponents.StyledTabPanel>
    ),
    renderTab: (props: any) => (
      <StyledComponents.StyledTab
        label={t('campaigns.enterTargets')}
        {...props}
      />
    ),
  });

  tabs.push({
    id: 'tab-2',
    panelId: 'panel-2',
    renderPanel: (props: any) => (
      <StyledComponents.StyledTabPanel {...props}>
        <div style={{ display: 'flex' }}>
          <div style={{ width: '43%', height: '200px' }}>
            <BrowseOrDragBulkUpload />
          </div>
          <div style={{ width: '50%' }}>
            <BulkUploadedDataView />
          </div>
        </div>
      </StyledComponents.StyledTabPanel>
    ),
    renderTab: (props: any) => (
      <StyledComponents.StyledTab
        label={t('campaigns.bulkUploadTargets')}
        {...props}
      />
    ),
  });
  const [activeItemIndex, setActiveItemIndex] = React.useState(0);

  return (<StyledComponents.StyledDialogContentBodyWrapper>
    <StyledComponents.StyledDialogContentBody data-testid='create-campaign-dialog-content'>
      <StyledComponents.StyledCreateCampaignElementWrapper>
        <PreviewButton />
        <CampaignEnablementStatusField />
        <Checkbox
          checked={showCanadaEnglishTranslation}
          dataTestId="show-canada-english-checkbox"
          id="newsletterCA"
          label={t('campaigns.showCanadaEnglish')}
          labelPosition="after"
          name="canadaEnglishShow"
          onChange={() => setShowCanadaEnglishTranslation(!showCanadaEnglishTranslation)}
          size="standard"
        />
        <Checkbox
          checked={showFrenchTranslation}
          dataTestId="show-french-checkbox"
          id="newsletter"
          label={t('campaigns.showCanadaFrench')}
          labelPosition="after"
          name="frenchShow"
          onChange={() => setShowFrenchTranslation(!showFrenchTranslation)}
          size="standard"
        />
      </StyledComponents.StyledCreateCampaignElementWrapper>

      <StyledComponents.StyledTranslateButtonsRow>
        <StyledComponents.StyledTranslateButtonWrapper>
          {showCanadaEnglishTranslation && campaignItems.description?.enUS && campaignItems.name?.enUS ? (
            <SecondaryButton
              dataTestId="campaign-translate-enca-button"
              size={BUTTON_SIZES.SMALL}
              iconColor="secondary"
              text={t('campaigns.translate')}
              onClick={() => handleTranslation('enCA')}
              isDisabled={
                archiveSwitch ||
                (campaignItems.id &&
                  (!permissions?.hasCampaignUpdatePermissions &&
                    !permissions?.hasCampaignSuperAdminPermissions))
              }
            />
          ) : (
            <div style={{ visibility: "hidden", width: "80px", height: "32px" }} />
          )}
        </StyledComponents.StyledTranslateButtonWrapper>

        <StyledComponents.StyledTranslateButtonWrapper>
          {showFrenchTranslation && campaignItems.description?.enUS && campaignItems.name?.enUS ? (
            <SecondaryButton
              dataTestId="campaign-translate-frca-button"
              size={BUTTON_SIZES.SMALL}
              iconColor="secondary"
              text={t('campaigns.translate')}
              onClick={() => handleTranslation('frCA')}
              isDisabled={
                archiveSwitch ||
                (campaignItems.id &&
                  (!permissions?.hasCampaignUpdatePermissions &&
                    !permissions?.hasCampaignSuperAdminPermissions))
              }
            />
          ) : (
            <div style={{ visibility: "hidden", width: "80px", height: "32px" }} />
          )}
        </StyledComponents.StyledTranslateButtonWrapper>
      </StyledComponents.StyledTranslateButtonsRow>
      {((showCanadaEnglishTranslation || showFrenchTranslation) && campaignItems.description?.enUS) && (
        <StyledComponents.CampaignTranslateDivider />
      )}
      <StyledComponents.StyledCreateCampaignElementWrapper>
        <CampaignFields.CampaignNameField />
        <CampaignFields.CampaignLinkTextField />
        <CampaignFields.CampaignLinkField />
      </StyledComponents.StyledCreateCampaignElementWrapper>
      <StyledComponents.StyledCreateCampaignElementWrapper>
        <CampaignFields.CampaignDescriptionField />
      </StyledComponents.StyledCreateCampaignElementWrapper>
      {showCanadaEnglishTranslation &&
        <>

          <StyledComponents.StyledCreateCampaignElementWrapper>
            <CampaignFields.CampaignNameEnCAField />
            <CampaignFields.CampaignLinkTextEnCAField />
          </StyledComponents.StyledCreateCampaignElementWrapper>

          <StyledComponents.StyledCreateCampaignElementWrapper>
            <CampaignFields.CampaignEnCADescriptionField />
          </StyledComponents.StyledCreateCampaignElementWrapper></>}
      {showFrenchTranslation &&
        <>

          <StyledComponents.StyledCreateCampaignElementWrapper>
            <CampaignFields.CampaignNameFrCAField />
            <CampaignFields.CampaignLinkTextFrCAField />
          </StyledComponents.StyledCreateCampaignElementWrapper>

          <StyledComponents.StyledCreateCampaignElementWrapper>
            <CampaignFields.CampaignFrDescriptionField />
          </StyledComponents.StyledCreateCampaignElementWrapper></>}
      <StyledComponents.StyledCreateCampaignElementWrapper>
        <CampaignFields.CampaignLogoImageUploadField />
      </StyledComponents.StyledCreateCampaignElementWrapper>
      <StyledComponents.StyledTargetSelectorContainer>
        <StyledComponents.StyledTabs
          activeItemIndex={activeItemIndex}
          alignment="left"
          items={tabs}
          onActiveItemChange={setActiveItemIndex}
          tabsLabel=""
          isDisabled={archiveSwitch || (campaignItems.id
            && (!permissions?.hasCampaignUpdatePermissions && !permissions?.hasCampaignSuperAdminPermissions))}
        />
      </StyledComponents.StyledTargetSelectorContainer>
    </StyledComponents.StyledDialogContentBody>
  </StyledComponents.StyledDialogContentBodyWrapper>
  );
};